import type { ServerRoutes } from "@/index";
import { hc } from "hono/client";

export const client = hc<ServerRoutes>("/");

// A 401 on a non-auth endpoint means the session expired or was revoked
// mid-session. Bounce to the login screen (the auth endpoints handle their own
// 401s — e.g. bad credentials — so they're excluded to avoid a redirect loop).
function handleUnauthorized(res: Response): void {
  if (res.status === 401 && !res.url.includes("/api/auth/")) {
    if (typeof window !== "undefined") window.location.assign("/login");
  }
}

export async function unwrap<T extends Response>(resPromise: Promise<T>): Promise<
  T extends Response & { json(): Promise<infer J> } ? J : never
> {
  const res = await resPromise;
  if (!res.ok) {
    handleUnauthorized(res);
    const err = (await res.json().catch(() => ({ error: res.statusText }))) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as never;
}

export async function expectNoContent(resPromise: Promise<Response>): Promise<void> {
  const res = await resPromise;
  if (!res.ok) {
    handleUnauthorized(res);
    const err = (await res.json().catch(() => ({ error: res.statusText }))) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
}

let weatherInflight: ReturnType<typeof fetchWeatherRaw> | null = null;
const wateringInflight = new Map<string, ReturnType<typeof fetchAssistantWateringRaw>>();

function fetchWeatherRaw() {
  return unwrap(client.api.weather.$get());
}

function fetchAssistantWateringRaw(potId: string) {
  return unwrap(client.api.assistants.watering[":potId"].$get({ param: { potId } }));
}

export function getWeather() {
  if (weatherInflight) return weatherInflight;
  weatherInflight = fetchWeatherRaw();
  return weatherInflight.finally(() => { weatherInflight = null; });
}

export function getAssistantWatering(potId: string) {
  const existing = wateringInflight.get(potId);
  if (existing) return existing;
  const p = fetchAssistantWateringRaw(potId).finally(() => { wateringInflight.delete(potId); });
  wateringInflight.set(potId, p);
  return p;
}

export function getSpecies() {
  return unwrap(client.api.species.$get());
}

export function searchSpecies(q: string) {
  return unwrap(client.api.assistants["species-explorer"].$post({ json: { q } }));
}

export function selectSearchedSpecies(input: { scientific: string; common: string }) {
  return unwrap(client.api.assistants["species-select"].$post({ json: input }));
}

export type SSEMessage = { event: string; data: unknown };

const SSE_MAX_BUFFER_BYTES = 1_048_576;

export async function* streamSSEPost(url: string, body: FormData): AsyncGenerator<SSEMessage, void, void> {
  const res = await fetch(url, { method: "POST", body });
  if (!res.ok) {
    handleUnauthorized(res);
    const err = (await res.json().catch(() => ({ error: res.statusText }))) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
  if (!res.body) throw new Error("Response has no body");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    if (buf.length > SSE_MAX_BUFFER_BYTES) {
      throw new Error(`SSE frame buffer exceeded ${SSE_MAX_BUFFER_BYTES} bytes without a frame terminator`);
    }
    let idx: number;
    while ((idx = buf.indexOf("\n\n")) !== -1) {
      const frame = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      let event = "message";
      let dataStr = "";
      for (const line of frame.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) dataStr += line.slice(5).replace(/^ /, "");
      }
      if (!dataStr) continue;
      yield { event, data: JSON.parse(dataStr) };
    }
  }
}
